/** Branch-pattern rules and the linear-time glob matcher (SPEC §4, §16.1). Pure functions, no zod. */

/** At most this many allowed branch patterns per environment, each at most `BRANCH_PATTERN_MAX_LENGTH` characters. */
export const BRANCH_PATTERNS_MAX = 20;
export const BRANCH_PATTERN_MAX_LENGTH = 100;

/**
 * Why `pattern` is not an allowed-branch glob, or `undefined` when it is one. The glob subset builds match
 * (`globMatches`, through apps/api/src/common/refs.ts): `*` is the only wildcard and matches any run of characters, `/`
 * included, so `release/*` matches `release/1.4` and `release/1.4/hotfix`; everything else is literal. Otherwise a
 * pattern is a git branch name (`git check-ref-format`): no whitespace or control characters, none of `~ ^ : \`, no
 * `..`, `//` or `@{`, and it neither starts with `-`, `/` or `.` nor ends with `/` or `.`. `?`, `[…]`, `{a,b}` and `!`
 * are refused because they read as glob syntax this subset does not have, and a full ref because builds send the
 * branch name (`main`, not `refs/heads/main`).
 */
export function branchPatternProblem(pattern: string): string | undefined {
  return BRANCH_PATTERN_RULES.find(([broken]) => broken(pattern))?.[1];
}

/** The rules of `branchPatternProblem`, checked in order: the first one a pattern breaks gives its message. */
const BRANCH_PATTERN_RULES: readonly (readonly [broken: (pattern: string) => boolean, message: string])[] = [
  [(p) => p.length === 0, 'must not be empty'],
  [(p) => p.trim() !== p, 'must not start or end with whitespace'],
  [(p) => p.length > BRANCH_PATTERN_MAX_LENGTH, `at most ${BRANCH_PATTERN_MAX_LENGTH} characters`],
  [(p) => /[\s\p{Cc}]/u.test(p), 'must not contain whitespace or control characters'],
  [(p) => /[?[\]{}!]/.test(p), 'only * is a wildcard: ?, [...], {a,b} and ! are not supported'],
  [(p) => /[~^:\\]/.test(p), 'must not contain ~, ^, : or \\ (git does not allow them in branch names)'],
  [(p) => /\.\.|\/\/|@\{/.test(p), 'must not contain "..", "//" or "@{"'],
  [(p) => /^[-/.]/.test(p) || /[/.]$/.test(p), 'must not start with "-", "/" or "." nor end with "/" or "."'],
  [(p) => p.startsWith('refs/'), 'name the branch (main), not the full ref (refs/heads/main)'],
];

/** `*` (or `**`): every branch. */
export const matchesEveryBranch = (pattern: string): boolean => /^\*+$/.test(pattern);

export interface GlobMatchOptions {
  /**
   * Whether `*` may cover a line break (`\n`, `\r`, U+2028, U+2029); default `true`. `false` answers exactly like the
   * anchored regular expression with `.*` for each `*` (`.` stops at a line break), which shadow `ignoreKeys` and OIDC
   * claim globs were matched with before they moved to this matcher.
   */
  starMatchesLineBreaks?: boolean;
}

const isLineBreak = (code: number): boolean => code === 0x0a || code === 0x0d || code === 0x2028 || code === 0x2029;

/**
 * Whether `value` matches the whole `glob` of this subset: `*` matches any run of characters, `/` included; everything
 * else is literal (`?`, `.`, `[` too). No regular expression is built from the glob: the literal runs between the `*`s
 * are placed left to right, each at its first occurrence after the previous one (`indexOf`), the last run pinned to the
 * end. The leftmost place never loses a match, because the next `*` absorbs whatever a later place would have skipped,
 * so nothing is retried and the time is linear in value + glob whatever the pattern. Globs are written by people
 * (environment branch patterns, shadow `ignoreKeys`, OIDC claim conditions); a backtracking regex for
 * `*a*a*a*a*a*a*a*a*b` against a long value blocks the event loop for seconds to minutes.
 */
export function globMatches(value: string, glob: string, options: GlobMatchOptions = {}): boolean {
  const runs = glob.split('*');
  if (runs.length === 1) return value === glob;
  const first = runs[0]!;
  const last = runs[runs.length - 1]!;
  const end = value.length - last.length; // where the last run starts
  if (end < first.length || !value.startsWith(first) || !value.endsWith(last)) return false;
  // A `*` covering value[from, to): allowed unless that holds a line break `*` may not cover.
  const starCovers = (options.starMatchesLineBreaks ?? true)
    ? (): boolean => true
    : (from: number, to: number): boolean => !hasLineBreak(value, from, to);
  const at = placeMiddleRuns(value, runs, end, starCovers);
  return at >= 0 && starCovers(at, end);
}

const hasLineBreak = (value: string, from: number, to: number): boolean => {
  for (let i = from; i < to; i++) if (isLineBreak(value.charCodeAt(i))) return true;
  return false;
};

/**
 * Places the literal runs between the first and the last one, each at its first occurrence after the previous one and
 * before `end`, with a `*` that may cover the gap between them. Returns where the last placed run ends, or `-1`.
 */
function placeMiddleRuns(value: string, runs: string[], end: number, starCovers: (from: number, to: number) => boolean): number {
  let at = runs[0]!.length;
  for (let i = 1; i < runs.length - 1; i++) {
    const run = runs[i]!;
    if (run === '') continue; // `**`
    const found = value.indexOf(run, at);
    if (found < 0 || found + run.length > end || !starCovers(at, found)) return -1;
    at = found + run.length;
  }
  return at;
}
